import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  Chip,
  Alert,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import { getDoctorsWaitingList } from "./services";
import { DoctorQueuePatientType } from "./types";
import { colors } from "../../../config";

const QUEUE_POLL_INTERVAL_MS = 10000;

const QUEUE_COLUMNS = [
  {
    key: "waitingList" as const,
    label: "Waiting",
    headerColor: colors.warningDark,
    bgColor: "#fff8e1",
    borderColor: "#ffc107",
  },
  {
    key: "emrStarted" as const,
    label: "EMR Started",
    headerColor: colors.primaryDark,
    bgColor: "#e3f2fd",
    borderColor: colors.primaryMain,
  },
  {
    key: "emrSubmitted" as const,
    label: "Checkout",
    headerColor: "#e65100",
    bgColor: "#fff3e0",
    borderColor: colors.orangeMain,
  },
];

function formatSystemTime(date: Date) {
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

function PatientQueueCard({ patient }: { patient: DoctorQueuePatientType }) {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 2,
        borderLeft: `4px solid ${colors.primaryMain}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <ConfirmationNumberIcon sx={{ fontSize: 18, color: colors.primaryMain }} />
        <Typography variant="subtitle1" fontWeight={700}>
          Token #{patient.tokenNo}
        </Typography>
        <Chip
          label={patient.status}
          size="small"
          sx={{ ml: "auto", textTransform: "capitalize", fontSize: "0.7rem" }}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <PersonIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <Typography variant="body1" fontWeight={600}>
          {patient.patientName}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <BadgeIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <Typography variant="body2" color="text.secondary">
          {patient.patientId}
        </Typography>
      </Box>
    </Paper>
  );
}

function DoctorWaitingQueue() {
  const { doctorId } = useParams<{ doctorId: string }>();
  const parsedDoctorId = parseInt(doctorId || "0", 10);
  const [systemTime, setSystemTime] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setSystemTime(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  const { data, isLoading, isError, refetch, isFetching } = useQuery(
    ["doctorWaitingList", parsedDoctorId],
    () => getDoctorsWaitingList(parsedDoctorId),
    {
      enabled: parsedDoctorId > 0,
      refetchInterval: QUEUE_POLL_INTERVAL_MS,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
    },
  );

  const queueData = data?.response;
  const columns = useMemo(
    () =>
      QUEUE_COLUMNS.map((column) => ({
        ...column,
        patients: queueData?.[column.key] ?? [],
      })),
    [queueData],
  );

  if (!parsedDoctorId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Invalid doctor id in URL.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: colors.grey100,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: colors.primaryMain,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Dr. {queueData?.doctorName || "—"}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Patient Queue
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccessTimeIcon />
          <Typography variant="h6" fontWeight={600}>
            {formatSystemTime(systemTime)}
          </Typography>
          {isFetching && !isLoading && (
            <CircularProgress size={18} sx={{ color: "#fff", ml: 1 }} />
          )}
        </Box>
      </Box>

      {isLoading && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {isError && !isLoading && (
        <Box sx={{ p: 4 }}>
          <Alert
            severity="error"
            action={
              <Typography
                component="button"
                onClick={() => refetch()}
                sx={{
                  border: 0,
                  bgcolor: "transparent",
                  cursor: "pointer",
                  textDecoration: "underline",
                  color: "inherit",
                }}
              >
                Retry
              </Typography>
            }
          >
            Failed to load patient queue.
          </Alert>
        </Box>
      )}

      {!isLoading && !isError && (
        <Box
          sx={{
            flex: 1,
            p: 2,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(3, 1fr)",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          {columns.map((column) => (
            <Paper
              key={column.key}
              elevation={0}
              sx={{
                height: "100%",
                minHeight: { md: "calc(100dvh - 120px)" },
                borderRadius: 2,
                overflow: "hidden",
                border: `1px solid ${column.borderColor}`,
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: column.bgColor,
                  borderBottom: `2px solid ${column.borderColor}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: column.headerColor }}
                >
                  {column.label}
                </Typography>
                <Chip
                  label={column.patients.length}
                  size="small"
                  sx={{
                    bgcolor: column.headerColor,
                    color: "#fff",
                    fontWeight: 700,
                  }}
                />
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  maxHeight: { md: "calc(100dvh - 180px)" },
                  overflowY: "auto",
                }}
              >
                {column.patients.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 4 }}
                  >
                    No patients
                  </Typography>
                ) : (
                  column.patients.map((patient) => (
                    <PatientQueueCard
                      key={`${column.key}-${patient.tokenNo}-${patient.patientId}`}
                      patient={patient}
                    />
                  ))
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default DoctorWaitingQueue;
